/**
 * The task form's draft — what survives a mobile pull-to-refresh.
 *
 * A manager fills the "משימה חדשה" form standing in a yard, with one thumb, on
 * a phone that treats a downward swipe as "reload the page". Before this module
 * that gesture threw the whole form away. So every keystroke the form takes is
 * mirrored into `localStorage`, which is synchronous and therefore still there
 * when the reloaded shell re-mounts the form a moment later.
 *
 * The draft is deliberately *not* the task: nothing here writes to Dexie and
 * nothing here creates a record. It is the unsent contents of one form, kept
 * under one key, restored once and then dropped — a save or a cancel clears it,
 * and so does a day passing, so a forgotten draft never resurfaces weeks later
 * on top of a form the operator opened for something else.
 *
 * Pure and storage-agnostic like {@link ./session-cache}, so the healthcheck
 * exercises the real implementation outside a browser.
 */
import { defaultStorage } from './session-cache';
import type { StorageLike } from './session-cache';

/**
 * The mandated key. Spelled flat rather than through `storageKey()` because the
 * field command names it exactly — it is still namespaced to this application,
 * which is all the isolation rule asks.
 */
export const TASK_DRAFT_KEY = 'shinua99_task_draft';

/** How long an abandoned draft is worth restoring. */
export const TASK_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

/** Every field of the task form that is worth carrying across a reload. */
export interface TaskDraftFields {
  title: string;
  taskTypeId: string;
  description: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:MM`, 24-hour. */
  startTime: string;
  estEndTime: string;
  priority: string;
  statusId: string;
  contactMode: 'saved' | 'manual';
  contactId: string;
  manualName: string;
  manualPhone: string;
  manualRole: string;
  destinationAddress: string;
  destinationLink: string;
  operationalNotes: string;
  internalNotes: string;
}

/** One saved draft: the fields, what they were being typed into, and when. */
export interface TaskDraft extends TaskDraftFields {
  /** `''` while a new task is being opened; the task id when one is edited. */
  taskId: string;
  savedAt: number;
}

/** A blank form — the shape every read is normalised to. */
export const EMPTY_TASK_DRAFT_FIELDS: TaskDraftFields = {
  title: '',
  taskTypeId: '',
  description: '',
  date: '',
  startTime: '',
  estEndTime: '',
  priority: '',
  statusId: '',
  contactMode: 'saved',
  contactId: '',
  manualName: '',
  manualPhone: '',
  manualRole: '',
  destinationAddress: '',
  destinationLink: '',
  operationalNotes: '',
  internalNotes: '',
};

/**
 * The fields that mean the operator actually typed something.
 *
 * The date and the times are excluded on purpose: the form opens with today
 * and 08:00–12:00 already in them, so treating those as content would save a
 * draft for a form nobody touched, and offer to restore it forever after.
 */
const TYPED_FIELDS: readonly (keyof TaskDraftFields)[] = [
  'title',
  'description',
  'contactId',
  'manualName',
  'manualPhone',
  'manualRole',
  'destinationAddress',
  'destinationLink',
  'operationalNotes',
  'internalNotes',
];

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** True when nothing worth keeping has been typed into the form yet. */
export function isDraftEmpty(fields: Partial<TaskDraftFields> | null | undefined): boolean {
  if (!fields) return true;
  return TYPED_FIELDS.every((field) => text(fields[field]).trim() === '');
}

/** Fills in whatever a stored draft is missing, so a read is always complete. */
export function normalizeDraftFields(
  fields: Partial<TaskDraftFields> | Record<string, unknown> | null | undefined,
): TaskDraftFields {
  const source = (fields ?? {}) as Record<string, unknown>;
  return {
    title: text(source.title),
    taskTypeId: text(source.taskTypeId),
    description: text(source.description),
    date: text(source.date),
    startTime: text(source.startTime),
    estEndTime: text(source.estEndTime),
    priority: text(source.priority),
    statusId: text(source.statusId),
    contactMode: source.contactMode === 'manual' ? 'manual' : 'saved',
    contactId: text(source.contactId),
    manualName: text(source.manualName),
    manualPhone: text(source.manualPhone),
    manualRole: text(source.manualRole),
    destinationAddress: text(source.destinationAddress),
    destinationLink: text(source.destinationLink),
    operationalNotes: text(source.operationalNotes),
    internalNotes: text(source.internalNotes),
  };
}

/** Narrows anything parsed off disk to a draft, or `null` if it is not one. */
export function parseTaskDraft(raw: string | null, now: number = Date.now()): TaskDraft | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;
  const savedAt = typeof candidate.savedAt === 'number' ? candidate.savedAt : NaN;
  if (!Number.isFinite(savedAt)) return null;
  // A draft older than the window is not offered — it is not this shift's work.
  if (now - savedAt > TASK_DRAFT_TTL_MS) return null;
  return {
    ...normalizeDraftFields(candidate),
    taskId: text(candidate.taskId),
    savedAt,
  };
}

/** Mirrors the form to disk. Called as the operator types. */
export function writeTaskDraft(
  draft: Partial<TaskDraftFields> & { taskId?: string },
  options: { now?: number; storage?: StorageLike | null } = {},
): TaskDraft {
  const entry: TaskDraft = {
    ...normalizeDraftFields(draft),
    taskId: text(draft.taskId),
    savedAt: options.now ?? Date.now(),
  };
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  try {
    storage?.setItem(TASK_DRAFT_KEY, JSON.stringify(entry));
  } catch {
    /* a full or blocked storage must never break typing into the form */
  }
  return entry;
}

export function readTaskDraft(
  options: { now?: number; storage?: StorageLike | null } = {},
): TaskDraft | null {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  if (!storage) return null;
  try {
    return parseTaskDraft(storage.getItem(TASK_DRAFT_KEY), options.now ?? Date.now());
  } catch {
    return null;
  }
}

/** Drops the draft — what a save and a cancel both do. */
export function clearTaskDraft(storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.removeItem(TASK_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The draft this very form may restore, or `null`.
 *
 * A draft belongs to the target it was typed into: the new-task draft is never
 * poured into the edit form of an existing task, and a draft of task A is never
 * offered while task B is open. An empty draft is no draft at all.
 */
export function restorableTaskDraft(
  taskId: string,
  options: { now?: number; storage?: StorageLike | null } = {},
): TaskDraft | null {
  const draft = readTaskDraft(options);
  if (!draft) return null;
  if (draft.taskId !== text(taskId)) return null;
  return isDraftEmpty(draft) ? null : draft;
}
